import { NumericTextBox } from '@syncfusion/ej2-inputs';
import { createElement, L10n } from '@syncfusion/ej2-base';
import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { WCharacterFormat, WParagraphFormat } from '../format/index';
import { WAbstractList } from '../list/abstract-list';
import { WLevelOverride } from '../list/level-override';
import { DropDownList } from '@syncfusion/ej2-dropdowns';
import { Tooltip } from '@syncfusion/ej2-popups';
import { ListViewModel } from './list-view-model';
/**
 * The List dialog is used to create or modify lists.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export class ListDialog {
    /**
     * @param {DocumentHelper} documentHelper - Specifies the document helper.
     * @private
     */
    constructor(documentHelper) {
        /**
         * @private
         */
        this.dialog = undefined;
        this.target = undefined;
        /**
         * @private
         */
        this.documentHelper = undefined;
        this.viewModel = undefined;
        this.startAt = undefined;
        this.textIndent = undefined;
        this.alignedAt = undefined;
        this.listLevelElement = undefined;
        this.followNumberWith = undefined;
        this.numberStyle = undefined;
        this.numberFormat = undefined;
        this.restartBy = undefined;
        this.startedValueChangedClickHandler = this.onStartedValueChangedClick.bind(this);
        this.textIndentChangedClickHandler = this.onTextIndentChangedClick.bind(this);
        this.alignedAtValueChangedClickHandler = this.onAlignedAtValueChangedClick.bind(this);
        this.numberFormatChangedClickHandler = this.onNumberFormatChangedClick.bind(this);
        /**
         * @private
         */
        this.isListCharacterFormat = false;
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onTextIndentChanged = (args) => {
            if (!args.isInteracted) {
                return;
            }
            this.viewModel.listLevel.paragraphFormat.leftIndent = args.value;
        };
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onStartValueChanged = (args) => {
            if (!args.isInteracted) {
                return;
            }
            if (!isNullOrUndefined(this.viewModel) && !isNullOrUndefined(this.viewModel.listLevel)) {
                this.viewModel.listLevel.startAt = args.value;
            }
        };
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onListLevelValueChanged = (args) => {
            this.viewModel.levelNumber = parseInt(args.value.slice(args.value.length - 1), 10) - 1;
            if (isNullOrUndefined(this.listLevel)) {
                return;
            }
            this.updateDialogValues();
            this.updateRestartLevelBox();
        };
        /**
         * @private
         * @param {any} args - Specifies the change event args.
         * @returns {void}
         */
        this.onNumberFormatChanged = (args) => {
            this.viewModel.listLevel.numberFormat = args.target.value;
        };
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onAlignedAtValueChanged = (args) => {
            if (!args.isInteracted) {
                return;
            }
            this.viewModel.listLevel.paragraphFormat.firstLineIndent =
                this.alignedAt.value - this.viewModel.listLevel.paragraphFormat.leftIndent;
        };
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onFollowCharacterValueChanged = (args) => {
            if (args.isInteracted) {
                this.viewModel.followCharacter = args.value;
            }
        };
        /**
         * @private
         * @param {ChangeEventArgs} args - Specifies the change event args.
         * @returns {void}
         */
        this.onLevelPatternValueChanged = (args) => {
            if (!args.isInteracted) {
                return;
            }
            this.viewModel.listLevelPattern = args.value;
            const numberFormat = '%' + (this.levelNumber + 1).toString();
            const numberFormatTextBox = document.getElementById(this.documentHelper.owner.containerId + '_numberFormat');
            if (this.listLevel.listLevelPattern === 'Bullet') {
                this.listLevel.numberFormat = String.fromCharCode(61623);
                numberFormatTextBox.value = this.listLevel.numberFormat;
            }
            else {
                if (this.listLevel.listLevelPattern === 'None') {
                    this.listLevel.numberFormat = '';
                }
                if (!this.listLevel.numberFormat.match(numberFormat) && this.listLevel.listLevelPattern !== 'None') {
                    this.listLevel.numberFormat = numberFormat + '.';
                }
                numberFormatTextBox.value = this.listLevel.numberFormat;
            }
        };
        /**
         * @private
         * @returns {void}
         */
        this.loadListDialog = () => {
            this.documentHelper.updateFocus();
            if (isNullOrUndefined(this.documentHelper.owner)) {
                return;
            }
            this.viewModel = new ListViewModel();
            this.viewModel.dialog = this;
            if (this.documentHelper.selection.paragraphFormat.listLevelNumber > 0) {
                this.viewModel.levelNumber = this.documentHelper.selection.paragraphFormat.listLevelNumber;
            }
            this.viewModel.list = this.documentHelper.selection.paragraphFormat.getList();
            if (isNullOrUndefined(this.listLevel)) {
                return;
            }
            this.updateDialogValues();
            if (this.documentHelper.selection.caret.style.display !== 'none') {
                this.documentHelper.selection.caret.style.display = 'none';
            }
        };
        /**
         * @private
         * @returns {void}
         */
        this.showFontDialog = () => {
            this.documentHelper.owner.fontDialogModule.showFontDialog(this.listLevel.characterFormat, true);
        };
        /**
         * @private
         * @returns {void}
         */
        this.onApplyList = () => {
            if (!isNullOrUndefined(this.owner)) {
                this.documentHelper.selection.paragraphFormat.setList(this.list, true);
            }
            this.documentHelper.dialog2.hide();
            this.documentHelper.updateFocus();
        };
        /**
         * @private
         * @returns {void}
         */
        this.onCancelButtonClick = () => {
            this.disposeBindingForListUI();
            this.documentHelper.dialog2.hide();
            this.documentHelper.updateFocus();
            this.isListCharacterFormat = false;
        };
        /**
         * @private
         * @returns {void}
         */
        this.closeListDialog = () => {
            this.disposeBindingForListUI();
            this.documentHelper.updateFocus();
            this.isListCharacterFormat = false;
        };
        this.documentHelper = documentHelper;
        this.viewModel = new ListViewModel();
    }
    /**
     * @private
     * @returns {WListLevel} Returns list level
     */
    get listLevel() {
        if (!isNullOrUndefined(this.viewModel)) {
            return this.viewModel.listLevel;
        }
        return undefined;
    }
    /**
     * @private
     * @returns {WList} Returns list
     */
    get list() {
        if (!isNullOrUndefined(this.viewModel)) {
            return this.viewModel.list;
        }
        return undefined;
    }
    /**
     * @private
     * @returns {number} Returns level number
     */
    get levelNumber() {
        if (this.listLevel.ownerBase instanceof WLevelOverride) {
            return this.listLevel.ownerBase.levelNumber;
            /* eslint-disable-next-line max-len */
        }
        else if (this.listLevel.ownerBase instanceof WAbstractList && !isNullOrUndefined(this.listLevel.ownerBase.levels)) {
            return this.listLevel.ownerBase.levels.indexOf(this.listLevel);
        }
        else {
            return -1;
        }
    }
    get owner() {
        return this.documentHelper.owner.viewer;
    }
    /**
     * @private
     * @returns {string} Returns module name
     */
    getModuleName() {
        return 'ListDialog';
    }
    /**
     * @private
     * @returns {void}
     */
    showListDialog() {
        const locale = new L10n('documenteditor', this.documentHelper.owner.defaultLocale);
        locale.setLocale(this.documentHelper.owner.locale);
        let bindEvent = false;
        if (!this.target) {
            this.initListDialog(locale, this.documentHelper.owner.enableRtl);
            bindEvent = true;
        }
        this.isListCharacterFormat = true;
        this.documentHelper.dialog2.header = locale.getConstant('Define new Multilevel list');
        this.documentHelper.dialog2.height = 'auto';
        this.documentHelper.dialog2.width = 'auto';
        this.documentHelper.dialog2.content = this.target;
        let buttonClass;
        const isRtl = this.documentHelper.owner.enableRtl;
        if (isRtl) {
            buttonClass = 'e-flat e-list-dlg-font e-de-dlg-target.e-de-rtl e-font-rtl';
        }
        else {
            buttonClass = 'e-flat e-list-dlg-font e-font';
        }
        this.documentHelper.dialog2.buttons = [{
                click: this.showFontDialog,
                buttonModel: { content: locale.getConstant('Font'), cssClass: buttonClass }
            }, {
                click: this.onApplyList,
                buttonModel: { content: locale.getConstant('Ok'), cssClass: 'e-flat e-list-dlg', isPrimary: true }
            },
            {
                click: this.onCancelButtonClick,
                buttonModel: { content: locale.getConstant('Cancel'), cssClass: 'e-flat e-list-dlg' }
            }];
        const dialogElement = this.documentHelper.dialog2.element;
        this.documentHelper.dialog2.dataBind();
        if (bindEvent) {
            this.wireAndBindEvent(locale, isRtl);
        }
        this.documentHelper.dialog2.beforeOpen = this.loadListDialog;
        this.documentHelper.dialog2.close = this.closeListDialog;
        this.documentHelper.dialog2.position = { X: 'center', Y: 'top' };
        this.documentHelper.dialog2.show();
    }
    /**
     * Shows the table properties dialog
     *
     * @private
     * @param {L10n} locale - Specifies the locale value
     * @param {boolean} isRtl - Specifies the is rtl
     * @returns {void}
     */
    initListDialog(locale, isRtl) {
        const containerId = this.documentHelper.owner.containerId;
        const id = containerId + '_insert_list';
        this.target = createElement('div', { id: id, className: 'e-de-list-dlg' });
        this.listLevelDiv = createElement('div', { innerHTML: '<label id="' + containerId + '_listLevellabel" style="display:block;" class=e-de-list-ddl-header-list-level>' + locale.getConstant('List level') + '</label><label id="' + containerId + '_modifyLabel" style="display:block;" class=e-de-list-ddl-subheader>' + locale.getConstant('Choose level to modify') + '</label><select style="height:20px;width:43%" id="' + containerId + '_listLevel"><option>' + locale.getConstant('Level') + ' 1' + '</option><option>' + locale.getConstant('Level') + ' 2' + '</option><option>' + locale.getConstant('Level') + ' 3' + '</option><option>' + locale.getConstant('Level') + ' 4' + '</option><option>' + locale.getConstant('Level') + ' 5' + '</option><option>' + locale.getConstant('Level') + ' 6' + '</option><option>' + locale.getConstant('Level') + ' 7' + '</option><option>' + locale.getConstant('Level') + ' 8' + '</option><option>' + locale.getConstant('Level') + ' 9' + '</option></select>' });
        this.target.appendChild(this.listLevelDiv);
        this.div = createElement('div');
        let divStyle;
        if (isRtl) {
            divStyle = '<div style="float:right;display:block;width:241px;">';
        }
        else {
            divStyle = '<div style="float:left;display:block;">';
        }
        this.numberStyleDiv = createElement('div', { innerHTML: divStyle + '<label id="' + containerId + '_numberFormatLabel" style="display:block;" class=e-de-list-ddl-header>' + locale.getConstant('Number format') + '</label><label id="' + containerId + '_numberStyleLabel" style="display:block;" class=e-de-list-ddl-subheader>' + locale.getConstant('Number style for this level') + '</label><select style="height:20px;width:100%" id="' + containerId + '_numberStyle"><option>' + locale.getConstant('Arabic') + '</option><option>' + locale.getConstant('UpRoman') + '</option><option>' + locale.getConstant('LowRoman') + '</option><option>' + locale.getConstant('UpLetter') + '</option><option>' + locale.getConstant('LowLetter') + '</option><option>' + locale.getConstant('Number') + '</option><option>' + locale.getConstant('Leading zero') + '</option><option>' + locale.getConstant('Bullet') + '</option><option>' + locale.getConstant('Ordinal') + '</option><option>' + locale.getConstant('Ordinal Text') + '</option><option>' + locale.getConstant('Special') + '</option><option>' + locale.getConstant('For East') + '</option></select><label id="' + containerId + '_startAtLabel" style="display:block;" class=e-de-list-ddl-subheaderbottom>' + locale.getConstant('Start at') + '</label><input type="text" id="' + containerId + '_startAt">' });
        this.div.appendChild(this.numberStyleDiv);
        this.numberFormatDiv = createElement('div', { className: 'e-de-list-dlg-subdiv', innerHTML: '<div><div><label id="' + containerId + '_formatLabel" style="display:inline-block;width:86%" class=e-de-list-ddl-subheader>' + locale.getConstant('Enter formatting for number') + '</label><button type="button" id="' + containerId + '_list_info" class="e-control e-btn e-primary e-de-list-format-info">i</button></div><input style=width:180px; type="text" id="' + containerId + '_numberFormat" aria-label="numberFormat" class=e-input></div><label id="' + containerId + '_restartLabel" style="display:block;" class=e-de-list-ddl-subheaderbottom>' + locale.getConstant('Restart list after') + '</label><select style="height:20px;width:100%" id="' + containerId + '_restartBy"><option>' + locale.getConstant('No Restart') + '</option></select></div>' });
        this.div.appendChild(this.numberFormatDiv);
        this.target.appendChild(this.div);
        let indentsDivLabelStyle;
        if (isRtl) {
            indentsDivLabelStyle = 'display:block;position:relative; ';
        }
        else {
            indentsDivLabelStyle = 'display:block; ';
        }
        this.indentsDiv = createElement('div', { innerHTML: divStyle + '<label id="' + containerId + '_IndentsLabel" style=' + indentsDivLabelStyle + 'class=e-de-list-ddl-header>' + locale.getConstant('Position') + '</label><label id="' + containerId + '_textIndentLabel" style=' + indentsDivLabelStyle + 'class=e-de-list-ddl-subheader>' + locale.getConstant('Text indent at') + '</label><input type="text" id="' + containerId + '_textIndent"><label id="' + containerId + '_followCharacterLabel" style=' + indentsDivLabelStyle + 'class=e-de-list-ddl-subheaderbottom>' + locale.getConstant('Follow number with') + '</label><select style="height:20px;width:100%" id="' + containerId + '_followCharacter"><option>' + locale.getConstant('Tab character') + '</option><option>' + locale.getConstant('Space') + '</option><option>' + locale.getConstant('Nothing') + '</option></select></div><div id="e-de-list-dlg-div" class="e-de-list-dlg-div"><label id="' + containerId + '_alignedAtLabel" style="display:block;" class=e-de-list-ddl-subheader>' + locale.getConstant('Aligned at') + '</label><input type="text" id="' + containerId + '_alignedAt"></div>' });
        this.target.appendChild(this.indentsDiv);
    }
    wireAndBindEvent(locale, isRtl) {
        const containerId = this.documentHelper.owner.containerId;
        if (isRtl) {
            document.getElementById('e-de-list-dlg-div').classList.add('e-de-rtl');
            this.numberFormatDiv.classList.add('e-de-rtl');
        }
        this.startAtTextBox = document.getElementById(containerId + '_startAt');
        this.startAtTextBox.setAttribute('aria-label', 'startAt');
        this.textIndentAtTextBox = document.getElementById(containerId + '_textIndent');
        this.textIndentAtTextBox.setAttribute('aria-label', 'textIndent');
        this.alignedAtTextBox = document.getElementById(containerId + '_alignedAt');
        this.alignedAtTextBox.setAttribute('aria-label', 'alignedAt');
        this.startAt = new NumericTextBox({
            format: '#',
            decimals: 0,
            min: 0,
            max: 50,
            width: '180px',
            enablePersistence: false
        });
        this.startAt.addEventListener('change', this.startedValueChangedClickHandler);
        this.startAt.appendTo(this.startAtTextBox);
        this.textIndent = new NumericTextBox({
            format: '#',
            decimals: 0,
            min: 0,
            max: 1584,
            width: '180px',
            step: 4,
            enablePersistence: false
        });
        this.textIndent.addEventListener('change', this.textIndentChangedClickHandler);
        this.textIndent.appendTo(this.textIndentAtTextBox);
        this.alignedAt = new NumericTextBox({
            format: '#',
            max: 1584,
            step: 6,
            width: '180px',
            enablePersistence: false
        });
        this.alignedAt.addEventListener('change', this.alignedAtValueChangedClickHandler);
        this.alignedAt.appendTo(this.alignedAtTextBox);
        this.listLevel1 = document.getElementById(containerId + '_listLevel');
        this.listLevelElement = new DropDownList({ popupHeight: '150px', width: '180px', enableRtl: isRtl, change: this.onListLevelValueChanged });
        this.listLevelElement.appendTo(this.listLevel1);
        this.followCharacterElement = document.getElementById(containerId + '_followCharacter');
        this.followNumberWith = new DropDownList({ popupHeight: '150px', width: '180px', enableRtl: isRtl, change: this.onFollowCharacterValueChanged });
        this.followNumberWith.appendTo(this.followCharacterElement);
        this.numberStyleEle = document.getElementById(containerId + '_numberStyle');
        this.numberStyle = new DropDownList({ popupHeight: '150px', width: '180px', enableRtl: isRtl, change: this.onLevelPatternValueChanged });
        this.numberStyle.appendTo(this.numberStyleEle);
        this.numberFormat = document.getElementById(containerId + '_numberFormat');
        this.numberFormat.addEventListener('change', this.numberFormatChangedClickHandler);
        this.restartElement = document.getElementById(containerId + '_restartBy');
        this.restartBy = new DropDownList({ popupHeight: '150px', width: '180px', enableRtl: isRtl });
        this.restartBy.appendTo(this.restartElement);
        this.button = document.getElementById(containerId + '_list_info');
        this.formatInfoToolTip = new Tooltip({ width: 200 });
        this.formatInfoToolTip.content = locale.getConstant('Number format tooltip information');
        this.formatInfoToolTip.position = 'RightTop';
        this.formatInfoToolTip.appendTo(this.button);
    }
    onTextIndentChangedClick(args) {
        this.onTextIndentChanged(args);
    }
    onStartedValueChangedClick(args) {
        this.onStartValueChanged(args);
    }
    onNumberFormatChangedClick(args) {
        this.onNumberFormatChanged(args);
    }
    onAlignedAtValueChangedClick(args) {
        this.onAlignedAtValueChanged(args);
    }
    updateRestartLevelBox() {
        const containerId = this.documentHelper.owner.containerId;
        const listLevel = document.getElementById(containerId + '_listLevel');
        const restartBy = document.getElementById(containerId + '_restartBy');
        for (let i = 0; i < restartBy.options.length; i) {
            restartBy.options.remove(i);
        }
        if (listLevel.selectedIndex === 0) {
            const option = document.createElement('option');
            option.value = 'No Restart';
            option.innerHTML = 'No Restart';
            restartBy.appendChild(option);
        }
        else {
            for (let i = listLevel.selectedIndex; i > 0; i--) {
                const option = document.createElement('option');
                option.value = 'Level ' + i;
                option.innerHTML = 'Level ' + i;
                restartBy.appendChild(option);
            }
            const option = document.createElement('option');
            option.value = 'No Restart';
            option.innerHTML = 'No Restart';
            restartBy.appendChild(option);
        }
        restartBy.selectedIndex = 0;
    }
    listPatternConverter(listLevelPattern) {
        switch (listLevelPattern) {
            case 'Arabic': return 0;
            case 'UpRoman': return 1;
            case 'LowRoman': return 2;
            case 'UpLetter': return 3;
            case 'LowLetter': return 4;
            case 'Number': return 5;
            case 'LeadingZero': return 6;
            case 'Bullet': return 7;
            case 'Ordinal': return 8;
            case 'OrdinalText': return 9;
            case 'Special': return 10;
            case 'FarEast': return 11;
            default: return 12;
        }
    }
    followCharacterConverter(followCharacter) {
        switch (followCharacter) {
            case 'Tab':
                return 0;
            case 'Space':
                return 1;
            default:
                return 2;
        }
    }
    calculateAlignedAt() {
        if (this.viewModel.listLevel.paragraphFormat.firstLineIndent < 0) {
            return this.viewModel.listLevel.paragraphFormat.leftIndent + this.viewModel.listLevel.paragraphFormat.firstLineIndent;
        }
        else {
            return this.viewModel.listLevel.paragraphFormat.firstLineIndent;
        }
    }
    updateDialogValues() {
        if (isNullOrUndefined(this.listLevel.characterFormat)) {
            this.listLevel.characterFormat = new WCharacterFormat(this.viewModel.listLevel, this.documentHelper.owner);
        }
        if (isNullOrUndefined(this.listLevel.paragraphFormat)) {
            this.listLevel.paragraphFormat = new WParagraphFormat(this.viewModel.listLevel);
        }
        //const restartByTextBox: HTMLSelectElement = document.getElementById(this.documentHelper.owner.containerId + '_restartBy') as HTMLSelectElement;
        if (!isNullOrUndefined(this.viewModel) && !isNullOrUndefined(this.viewModel.listLevel)) {
            this.startAt.value = this.viewModel.listLevel.startAt;
            this.textIndent.value = this.viewModel.listLevel.paragraphFormat.leftIndent;
            this.alignedAt.value = this.calculateAlignedAt();
            this.followNumberWith.index = this.followCharacterConverter(this.viewModel.followCharacter);
            this.numberFormat.value = this.viewModel.listLevel.numberFormat;
            this.numberFormat.style.fontFamily = this.viewModel.listLevel.characterFormat.fontFamily;
            this.numberStyle.index = this.listPatternConverter(this.viewModel.listLevelPattern);
            this.listLevelElement.index = this.viewModel.levelNumber;
            //this.viewModel.levelNumber = this.viewModel.levelNumber;
        }
    }
    /**
     * @param {WCharacterFormat} format - Specifies the character format.
     * @private
     * @returns {void}
     */
    updateCharacterFormat(format) {
        this.listLevel.characterFormat.copyFormat(format);
    }
    disposeBindingForListUI() {
        this.followNumberWith.index = -1;
        this.numberFormat.value = ' ';
        this.numberStyle.index = -1;
        this.listLevelElement.index = -1;
        this.restartBy.index = -1;
        this.viewModel.destroy();
    }
    /**
     * @private
     * @returns {void}
     */
    destroy() {
        if (this.alignedAt) {
            this.alignedAt.destroy();
        }
        this.alignedAt = undefined;
        this.dialog = undefined;
        if (this.followNumberWith) {
            this.followNumberWith.destroy();
        }
        this.followNumberWith = undefined;
        if (this.listLevelElement) {
            this.listLevelElement.destroy();
        }
        this.listLevelElement = undefined;
        if (this.textIndent) {
            this.textIndent.destroy();
        }
        this.textIndent = undefined;
        if (this.startAt) {
            this.startAt.destroy();
        }
        this.startAt = undefined;
        if (this.numberStyle) {
            this.numberStyle.destroy();
        }
        this.numberStyle = undefined;
        this.numberFormat = undefined;
        if (this.restartBy) {
            this.restartBy.destroy();
        }
        this.restartBy = undefined;
        if (!isNullOrUndefined(this.target)) {
            if (this.target.parentElement) {
                this.target.parentElement.removeChild(this.target);
            }
            for (let l = 0; l < this.target.childNodes.length; l++) {
                this.target.removeChild(this.target.childNodes[parseInt(l.toString(), 10)]);
                l--;
            }
            this.target = undefined;
        }
        this.removeEvents();
        this.removeElements();
        this.documentHelper = undefined;
        this.viewModel = undefined;
    }
    removeEvents() {
        if (this.startAt) {
            this.startAt.removeEventListener('change', this.startedValueChangedClickHandler);
        }
        if (this.textIndent) {
            this.textIndent.removeEventListener('change', this.textIndentChangedClickHandler);
        }
        if (this.alignedAt) {
            this.alignedAt.removeEventListener('change', this.alignedAtValueChangedClickHandler);
        }
        if (this.numberFormat) {
            this.numberFormat.removeEventListener('change', this.numberFormatChangedClickHandler);
        }
    }
    removeElements() {
        if (this.listLevelDiv) {
            this.listLevelDiv.remove();
            this.listLevelDiv = undefined;
        }
        if (this.div) {
            this.div.remove();
            this.div = undefined;
        }
        if (this.numberStyleDiv) {
            this.numberStyleDiv.remove();
            this.numberStyleDiv = undefined;
        }
        if (this.indentsDiv) {
            this.indentsDiv.remove();
            this.indentsDiv = undefined;
        }
        if (this.numberFormatDiv) {
            this.numberFormatDiv.remove();
            this.numberFormatDiv = undefined;
        }
        if (this.startAtTextBox) {
            this.startAtTextBox.remove();
            this.startAtTextBox = undefined;
        }
        if (this.textIndentAtTextBox) {
            this.textIndentAtTextBox.remove();
            this.textIndentAtTextBox = undefined;
        }
        if (this.alignedAtTextBox) {
            this.alignedAtTextBox.remove();
            this.alignedAtTextBox = undefined;
        }
        if (this.listLevel1) {
            this.listLevel1.remove();
            this.listLevel1 = undefined;
        }
        if (this.followCharacterElement) {
            this.followCharacterElement.remove();
            this.followCharacterElement = undefined;
        }
        if (this.numberStyleEle) {
            this.numberStyleEle.remove();
            this.numberStyleEle = undefined;
        }
        if (this.restartElement) {
            this.restartElement.remove();
            this.restartElement = undefined;
        }
        if (this.button) {
            this.button.remove();
            this.button = undefined;
        }
        if (this.formatInfoToolTip) {
            this.formatInfoToolTip.destroy();
            this.formatInfoToolTip = undefined;
        }
        if (this.startAtTextBox) {
            this.startAtTextBox.remove();
            this.startAtTextBox = undefined;
        }
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
